import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import Footer from '@/components/sections/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Calendar, Search, MessageSquare, Clock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loadingInquiries, setLoadingInquiries] = useState(true);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (token) {
      fetchInquiries();
    }
  }, [token]);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/me/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data.inquiries || []);
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    } finally {
      setLoadingInquiries(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" /> Pending</Badge>;
      case 'responded':
        return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle className="w-3 h-3" /> Responded</Badge>;
      case 'closed':
        return <Badge variant="outline" className="gap-1"><XCircle className="w-3 h-3" /> Closed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <ManufacturerLogos />
      
      <main className="flex-1 container mx-auto px-4 py-8" data-testid="buyer-dashboard">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome back, {user.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            Manage your account and view your quote requests
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Profile Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{user.name}</p>
                  <Badge variant="outline" className="mt-1">Buyer Account</Badge>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span>{user.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>Member since {formatDate(user.created_at)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Start exploring our marketplace</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <Button 
                  onClick={() => navigate('/marketplace')}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  data-testid="browse-drones-btn"
                >
                  <Search className="w-6 h-6" />
                  <span>Browse Drones</span>
                </Button>
                <Button 
                  onClick={() => navigate('/sell')}
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  data-testid="list-drone-btn"
                >
                  <MessageSquare className="w-6 h-6" />
                  <span>List a Drone to Sell</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Inquiries Section */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Your Quote Requests
              </CardTitle>
              <CardDescription>
                Track the status of your drone inquiries
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingInquiries ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : inquiries.length === 0 ? (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No inquiries yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Browse our marketplace and request quotes on drones you're interested in
                  </p>
                  <Button onClick={() => navigate('/marketplace')} data-testid="start-browsing-btn">
                    Start Browsing
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div 
                      key={inquiry.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold">{inquiry.drone_model}</h4>
                          {getStatusBadge(inquiry.status)}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          Interest: {inquiry.interest}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Submitted {formatDate(inquiry.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
